/* Feb-2026 — Pre-launch banner.
 *
 * Painted over the top of the landing + bookstore pages while the site
 * is in test mode. Content is admin-editable via PUT /api/admin/landing
 * with `{ site_mode: { banner_enabled: false, banner_title, banner_subtitle } }`.
 *
 * Interaction model: `pointer-events-none` on the outer wrapper so
 * clicks pass THROUGH to the page below. Individual interactive
 * children re-enable pointer events (`pointer-events-auto`) locally —
 * only the tiny "×" dismiss button needs it, and even then dismiss is
 * per-session (sessionStorage) not persistent, so admins can flip the
 * DB flag to hide it site-wide.
 *
 * Transparent styling: 40% alpha oxblood-cream gradient, subtle
 * drop-shadow, `backdrop-blur-sm` so words behind stay legible.
 */
import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'cnr:pre-launch-banner-dismissed';

// Feb-2026 iter — routes where the banner MUST NOT paint (admin hub,
// admin sub-panels, editor shell, and other authenticated back-office
// views). Keeps the launch-mode banner strictly on public-facing
// pages the customer actually sees.
const HIDE_ON_PREFIXES = ['/admin', '/bookstore/admin', '/editor', '/quill'];

export default function PreLaunchBanner() {
  const location = useLocation();
  const [cfg, setCfg] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (window.sessionStorage.getItem(SESSION_KEY) === '1') setDismissed(true);
    } catch { /* private mode */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/landing`)
      .then((r) => { if (!cancelled) setCfg(r.data?.site_mode || null); })
      .catch(() => { /* silent — banner just won't render */ });
    return () => { cancelled = true; };
  }, []);

  // Hide entirely on admin / back-office / editor routes so the
  // launch-mode banner never overlays the operator UI.
  const path = location?.pathname || '';
  if (HIDE_ON_PREFIXES.some((p) => path === p || path.startsWith(p + '/'))) {
    return null;
  }
  if (!cfg || !cfg.banner_enabled || dismissed) return null;

  const dismiss = () => {
    try { window.sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      // pointer-events-none: everything behind the banner stays fully
      // clickable / selectable / focusable. The dismiss button below
      // opts back in with pointer-events-auto.
      //
      // Positioning: `fixed inset-0` + centered flex so the banner sits
      // dead-centre of the viewport and stays there while the user
      // scrolls. Mounted at the App root, so it overlays every route
      // (landing, bookstore, and the Loom app shell).
      className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center px-4 sm:px-6"
      role="status"
      aria-live="polite"
      data-testid="pre-launch-banner"
    >
      <div
        // Transparent oxblood/cream gradient over subtle blur so the
        // content behind stays visible. Interactive elements inside
        // this box are individually re-enabled with pointer-events-auto.
        className="relative w-full max-w-2xl rounded-lg border-2 border-[#c6a664]/70 bg-gradient-to-b from-[#5a1c1f]/55 to-[#3d1213]/50 px-6 py-5 text-center shadow-2xl backdrop-blur-[3px] sm:px-10 sm:py-6"
      >
        <button
          type="button"
          aria-label="Dismiss test-mode banner for this session"
          data-testid="pre-launch-banner-dismiss"
          onClick={dismiss}
          className="pointer-events-auto absolute right-1.5 top-1.5 rounded p-1 text-[#f8f5ee]/70 transition hover:bg-white/10 hover:text-[#f8f5ee] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c6a664]/70"
        >
          <X className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
        <div
          className="pointer-events-none select-text text-[15px] font-semibold uppercase tracking-[0.14em] text-[#f8f5ee] sm:text-[18px]"
          style={{ fontFamily: 'Georgia, "Playfair Display", serif' }}
        >
          {cfg.banner_title}
        </div>
        <div
          className="pointer-events-none mt-2 select-text text-[12.5px] leading-relaxed text-[#f8f5ee]/95 sm:text-[14px]"
          style={{ fontFamily: 'Georgia, "Playfair Display", serif' }}
        >
          {cfg.banner_subtitle}
        </div>
      </div>
    </div>
  );
}
